import { localCalendarDateToIsoDateString } from "helpers/date";
import { IContractHistory } from "store/queries/contractFeeSchedules";

// Contract History (activity log) display helpers.
export const HISTORY_TYPE_SEVERITY: Record<
  string,
  "success" | "info" | "warning" | "danger"
> = {
  Created: "success",
  Updated: "info",
  ProceduresRegenerated: "warning",
  // CLE-2884 — the type the API actually writes (Prisma has no ProceduresRegenerated).
  ProceduresGenerated: "warning",
  AttachmentAdded: "success",
  AttachmentRemoved: "danger",
  CarveoutAdded: "success",
  CarveoutRemoved: "danger",
  Activated: "success",
};

export const HISTORY_TYPE_LABELS: Record<string, string> = {
  Created: "Created",
  Updated: "Updated",
  ProceduresRegenerated: "Procedures Regenerated",
  AttachmentAdded: "Attachment Added",
  AttachmentRemoved: "Attachment Removed",
  CarveoutAdded: "Carve-out Added",
  CarveoutRemoved: "Carve-out Removed",
  ProceduresGenerated: "Procedures Generated",
  Activated: "Activated",
};

export const HISTORY_FIELD_LABELS: Record<string, string> = {
  providerId: "Provider",
  payerId: "Payer",
  effectiveDate: "Effective Date",
  nextUpdateDate: "Next Update Date",
  updateTerm: "Update Term",
  updatePercent: "Percent Increase",
  notes: "Notes",
  implantTerms: "Implant Terms",
  // CLE-2811 — contract facts. Written both by hand on this form and by the AI modeling tool's
  // apply step, so the activity log is where a human sees which one moved a value.
  contractType: "Contract Type",
  signedDate: "Signed Date",
  timelyFilingDays: "Timely Filing (days)",
  inpatientReimbursement: "Inpatient Reimbursement",
  outpatientReimbursement: "Outpatient Reimbursement",
  facilityReimbursement: "Facility Reimbursement",
  nonFacilityReimbursement: "Non-Facility Reimbursement",
  // CLE-2811 (2026-09-28) — its own column; the AI tool's save used to append it to notes.
  multipleProcedureMethodology: "Multiple Procedure Methodology",
  // Legacy read-only (no longer produced after CLE-2525) — kept so old audit rows still render.
  mpdrRulesetIds: "MPPR Rulesets",
  caseRateRulesetIds: "Case Rate Rulesets",
  explodingFeeScheduleRulesetIds: "Exploding Fee Schedule Rulesets",
  packageRulesetIds: "Package Rulesets",
  inpatientRulesetIds: "Inpatient Rulesets",
  // CLE-2751: also carries linked Medicaid payers, so the history label is the generic one.
  medicareAdvantagePayerIds: "Linked Payers",
  // CLE-2786: providers sharing this contract instead of holding a duplicate of it.
  linkedProviderIds: "Connected Providers",
  rateCalculations: "Rate Calculations",
  baseRateFileKind: "File Type",
  carveoutRate: "Carve-out Rate",
};

export type ContractUpdateTerm = "MedicareAnnual" | "PercentIncrease";

export interface IUpdateTerms {
  updateTerm: ContractUpdateTerm | null;
  updatePercent: number | null;
}

/**
 * CLE-2781 — coerce the update-term pair into one of the three shapes the API accepts, mirroring
 * `assertValidUpdateTerms` on the backend:
 *
 *   no next update date  → no term, no percent
 *   MedicareAnnual       → no percent
 *   PercentIncrease      → the percent as entered (0 included — a real negotiated outcome)
 *
 * The form calls this on every change AND again at submit, because the two ways to leave a stale
 * value behind are switching PercentIncrease → MedicareAnnual and clearing the date after a term
 * was chosen. Either one would otherwise send the API a body it rejects.
 */
export const normalizeUpdateTerms = (
  nextUpdateDate: Date | null | undefined,
  terms: Partial<IUpdateTerms>
): IUpdateTerms => {
  if (!nextUpdateDate) return { updateTerm: null, updatePercent: null };
  if (terms.updateTerm !== "PercentIncrease") {
    return { updateTerm: terms.updateTerm ?? null, updatePercent: null };
  }
  return {
    updateTerm: "PercentIncrease",
    updatePercent: terms.updatePercent ?? null,
  };
};

/**
 * CLE-2781 — how a contract's rates move at the next update date. The dropdown options and the
 * display labels come from the same place so the list column and the form cannot drift apart.
 */
export const UPDATE_TERM_LABELS: Record<string, string> = {
  MedicareAnnual: "Medicare Annual",
  PercentIncrease: "Percent Increase",
};

export const UPDATE_TERM_OPTIONS = Object.entries(UPDATE_TERM_LABELS).map(
  ([value, label]) => ({ label, value })
);

/**
 * Render an update term for display: `Medicare Annual`, or `Increase: 3%` (CLE-2788 — Ani
 * asked for the short form wherever the number is shown; the picker still says "Percent Increase").
 *
 * Returns null when there is no term, so callers can render their own empty state (the list
 * view shows a dash, the detail view shows "Not set") rather than a hardcoded string.
 */
export const formatUpdateTerm = (
  updateTerm: string | null | undefined,
  updatePercent: number | string | null | undefined
): string | null => {
  if (!updateTerm) return null;
  const label = UPDATE_TERM_LABELS[updateTerm] ?? updateTerm;
  if (updateTerm !== "PercentIncrease") return label;
  // 0 is a real negotiated outcome, so test for null/undefined rather than falsiness. The API
  // sends a Prisma Decimal, which arrives as a string.
  if (
    updatePercent === null ||
    updatePercent === undefined ||
    updatePercent === ""
  ) {
    return label;
  }
  const pct = Number(updatePercent);
  if (Number.isNaN(pct)) return label;
  // Trim a trailing `.00` so `3.00` from the database reads as `3%`, but keep `3.5%`.
  return `Increase: ${Number(pct.toFixed(2))}%`;
};

/** Render a `{ ratePercent, rateType }` pair as `$12.50/unit` or `85%`. */
export const formatRate = (
  ratePercent: number | null | undefined,
  rateType: unknown
): string => {
  if (ratePercent === null || ratePercent === undefined) return "Not set";
  const value = Number(ratePercent);
  return rateType === "unit"
    ? `$${(value / 100).toFixed(2)}/unit`
    : `${value}%`;
};

/**
 * Render a history `from`/`to` value as readable text (handles provider/payer name objects,
 * arrays, rate calcs). Pass `field` for values that only read well with their field's context —
 * CLE-2781's `updateTerm` stores the enum key (`PercentIncrease`) and `updatePercent` a bare
 * number, neither of which an analyst should see raw.
 */
export const formatHistoryValue = (value: unknown, field?: string): string => {
  if (value === null || value === undefined || value === "") return "Not set";
  if (field === "updateTerm" && typeof value === "string") {
    return UPDATE_TERM_LABELS[value] ?? value;
  }
  if (field === "updatePercent") {
    const pct = Number(value);
    return Number.isNaN(pct) ? String(value) : `${Number(pct.toFixed(2))}%`;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "None";
    return value
      .map((v) => {
        if (v && typeof v === "object") {
          const o = v as any;
          if (o.name) return o.name as string;
          if (o.ratePercent !== undefined) {
            return formatRate(o.ratePercent, o.rateType);
          }
          // Id-bearing object whose name didn't resolve (e.g. deleted ruleset).
          if (o.id !== undefined) return "Unnamed";
          return JSON.stringify(o);
        }
        return String(v);
      })
      .join(", ");
  }
  if (typeof value === "object") {
    const o = value as any;
    if (o.name) return o.name as string;
    // Carve-out rate edits (CLE-2573) log a bare { ratePercent, rateType } pair.
    if (o.ratePercent !== undefined)
      return formatRate(o.ratePercent, o.rateType);
    if (o.id !== undefined) return "Unnamed";
    return JSON.stringify(o);
  }
  return String(value);
};

/**
 * Readable label for a single rate calculation: base rate file name + its rate.
 * The name comes from the history snapshot itself (`displayName`), which now
 * carries the base rate file name alongside the ids.
 */
const formatRateCalculationLabel = (calc: any): string => {
  const name =
    calc?.displayName?.trim() ||
    calc?.baseRateFile?.displayName?.trim() ||
    "Unnamed";
  if (calc?.ratePercent === undefined || calc?.ratePercent === null) {
    return name;
  }
  const ratePercent = Number(calc.ratePercent);
  const rate =
    calc.rateType === "unit"
      ? `$${(ratePercent / 100).toFixed(2)}/unit`
      : `${ratePercent}%`;
  return `${name} ${rate}`;
};

/**
 * Diff two rate-calculation array snapshots (history `from`/`to`), identified by
 * base rate file. Names come from each snapshot's `displayName`.
 *
 * `changed` covers files that stayed attached but had their rate corrected
 * (CLE-2573) — without it an in-place rate edit reads as "No changes".
 */
export const diffRateCalculations = (
  from: unknown,
  to: unknown
): { added: string[]; removed: string[]; changed: string[] } => {
  const fromArr = Array.isArray(from) ? (from as any[]) : [];
  const toArr = Array.isArray(to) ? (to as any[]) : [];
  const keyOf = (calc: any): string => calc?.baseRateFileId;
  const fromKeys = new Set(fromArr.map(keyOf));
  const toByKey = new Map(toArr.map((calc) => [keyOf(calc), calc] as const));
  const sameRate = (a: any, b: any): boolean =>
    Number(a?.ratePercent) === Number(b?.ratePercent) &&
    (a?.rateType ?? "percent") === (b?.rateType ?? "percent");

  return {
    added: toArr
      .filter((calc) => !fromKeys.has(keyOf(calc)))
      .map(formatRateCalculationLabel),
    removed: fromArr
      .filter((calc) => !toByKey.has(keyOf(calc)))
      .map(formatRateCalculationLabel),
    changed: fromArr
      .filter((calc) => {
        const next = toByKey.get(keyOf(calc));
        return next !== undefined && !sameRate(calc, next);
      })
      .map((calc) => {
        const next = toByKey.get(keyOf(calc));
        return `${formatRateCalculationLabel(calc)} → ${formatRate(
          next?.ratePercent,
          next?.rateType
        )}`;
      }),
  };
};

/** History fields that are named lists (e.g. rulesets) — shown as added/removed diffs. */
export const HISTORY_LIST_FIELDS = new Set([
  // The five *RulesetIds entries are legacy read-only (no longer produced after CLE-2525).
  "mpdrRulesetIds",
  "caseRateRulesetIds",
  "explodingFeeScheduleRulesetIds",
  "packageRulesetIds",
  "inpatientRulesetIds",
  "medicareAdvantagePayerIds",
  "linkedProviderIds",
]);

/**
 * Diff two snapshots of a named list (history `from`/`to`) into added/removed
 * label lists. Items are identified by `id` (falling back to their name/value)
 * and labelled by `name`.
 */
export const diffNamedList = (
  from: unknown,
  to: unknown
): { added: string[]; removed: string[] } => {
  const fromArr = Array.isArray(from) ? (from as any[]) : [];
  const toArr = Array.isArray(to) ? (to as any[]) : [];
  const keyOf = (item: any): string =>
    item && typeof item === "object"
      ? item.id ?? item.name ?? JSON.stringify(item)
      : String(item);
  const labelOf = (item: any): string => {
    if (!item || typeof item !== "object") return String(item);
    const name = item.name ?? "Unnamed";
    if (item.providerName || item.payerName) {
      return `${name} (${item.providerName ?? ""}-${item.payerName ?? ""})`;
    }
    return name;
  };
  const fromKeys = new Set(fromArr.map(keyOf));
  const toKeys = new Set(toArr.map(keyOf));
  return {
    added: toArr.filter((item) => !fromKeys.has(keyOf(item))).map(labelOf),
    removed: fromArr.filter((item) => !toKeys.has(keyOf(item))).map(labelOf),
  };
};

/** CLE-2884 — changed-field labels a history summary line names before "+N more". */
export const HISTORY_SUMMARY_MAX_FIELDS = 3;

/**
 * The file name an entry's `valueChanges` carries under `carveout` or `attachments`, when the
 * history card would render one (same truthiness test it uses).
 */
const historyEntryName = (
  entry: IContractHistory,
  key: "carveout" | "attachments"
): string | undefined => {
  const name = (entry.valueChanges as any)?.[key]?.name;
  return name ? String(name) : undefined;
};

const isCarveoutHistoryType = (type: IContractHistory["type"]) =>
  type === "CarveoutAdded" || type === "CarveoutRemoved";

const isAttachmentHistoryType = (type: IContractHistory["type"]) =>
  type === "AttachmentAdded" || type === "AttachmentRemoved";

/**
 * CLE-2884 — Ani: "in history only show one line per history collapse rest". The text after the
 * badge, user and date on an entry's single line: what changed, at a glance. The details behind
 * the chevron still say how.
 */
export const summarizeContractHistoryEntry = (
  entry: IContractHistory
): string => {
  if (entry.type === "Updated") {
    const carveoutName = historyEntryName(entry, "carveout");
    const labels = Object.keys(entry.valueChanges ?? {})
      // `carveout` names the file the other fields refer to; it is not a change of its own.
      .filter((field) => field !== "carveout")
      .map((field) =>
        field === "carveoutRate" && carveoutName
          ? `Carve-out rate: ${carveoutName}`
          : HISTORY_FIELD_LABELS[field] ?? field
      );
    if (labels.length <= HISTORY_SUMMARY_MAX_FIELDS) return labels.join(", ");
    return `${labels.slice(0, HISTORY_SUMMARY_MAX_FIELDS).join(", ")} +${
      labels.length - HISTORY_SUMMARY_MAX_FIELDS
    } more`;
  }
  if (isCarveoutHistoryType(entry.type)) {
    const name = historyEntryName(entry, "carveout");
    return name ? `Carve-out: ${name}` : "";
  }
  if (isAttachmentHistoryType(entry.type)) {
    const name = historyEntryName(entry, "attachments");
    return name ? `Attachment: ${name}` : "";
  }
  return entry.description ?? "";
};

/**
 * CLE-2884 — whether an entry's collapsed details would add anything to its line, so the chevron
 * appears only where it opens something. A carve-out or attachment entry's file name is already
 * its summary, so like every other non-update entry it opens only for a description; an update
 * also opens for its carve-out name or any field change.
 */
export const contractHistoryEntryHasDetails = (
  entry: IContractHistory
): boolean => {
  if (entry.description) return true;
  if (entry.type !== "Updated") return false;
  return (
    !!historyEntryName(entry, "carveout") ||
    Object.keys(entry.valueChanges ?? {}).some((field) => field !== "carveout")
  );
};

/** CLE-2811 — the reimbursement schedule's slots. A contract type uses one or two of them. */
export type ReimbursementSlot =
  | "inpatient"
  | "outpatient"
  | "facility"
  | "nonFacility";

/**
 * CLE-2811 — which reimbursement slots a contract type actually has.
 *
 * The shape differs by facility kind, and showing all four on every contract would invite someone to
 * fill "inpatient reimbursement" on a surgery center. An unset type has none, deliberately: pick the type
 * first, then the boxes that belong to it appear.
 */
export const REIMBURSEMENT_SLOTS_BY_CONTRACT_TYPE: Record<
  string,
  ReimbursementSlot[]
> = {
  Hospital: ["inpatient", "outpatient"],
  CAH: ["inpatient", "outpatient"],
  ASC: ["outpatient"],
  Professional: ["facility", "nonFacility"],
};

/** The contract column each slot is stored in. */
export const REIMBURSEMENT_FIELD_KEYS: Record<ReimbursementSlot, string> = {
  inpatient: "inpatientReimbursement",
  outpatient: "outpatientReimbursement",
  facility: "facilityReimbursement",
  nonFacility: "nonFacilityReimbursement",
};

const SLOT_WORDS: Record<ReimbursementSlot, string> = {
  inpatient: "Inpatient",
  outpatient: "Outpatient",
  facility: "Facility",
  nonFacility: "Non-facility",
};

/** The one slot of a single-slot contract type (ASC) is simply the reimbursement schedule. */
export const REIMBURSEMENT_SCHEDULE = "Reimbursement schedule";

/**
 * CLE-2811 (2026-09-28) — Ani: "Outpatient reimbursement → Reimbursement Schedule". The label of one slot,
 * the same on the run form, the save panel and the contract's own pages:
 *
 * - a type with ONE slot (ASC → outpatient): "Reimbursement schedule" — there is nothing to tell it from;
 * - a type with two: "Inpatient / Outpatient reimbursement schedule" (Hospital, CAH), "Facility /
 *   Non-facility reimbursement schedule" (Professional);
 * - an unknown type, or a slot the type does not use: the two-slot wording, which is never ambiguous.
 */
export const reimbursementSlotLabel = (
  slot: ReimbursementSlot,
  contractType?: string | null
): string => {
  const slots =
    (contractType && REIMBURSEMENT_SLOTS_BY_CONTRACT_TYPE[contractType]) ||
    null;
  if (slots && slots.length === 1 && slots[0] === slot)
    return REIMBURSEMENT_SCHEDULE;
  return `${SLOT_WORDS[slot]} reimbursement schedule`;
};

/**
 * The slot a field names, whichever way it is spelled: the contract's column ("outpatientReimbursement")
 * or the contract term's path ("reimbursement.outpatient"). Null for any other field.
 */
export const reimbursementSlotOf = (
  field: string
): ReimbursementSlot | null => {
  const slots = Object.keys(SLOT_WORDS) as ReimbursementSlot[];
  return (
    slots.find(
      (slot) =>
        field === REIMBURSEMENT_FIELD_KEYS[slot] ||
        field === `reimbursement.${slot}`
    ) ?? null
  );
};

/**
 * CLE-2811 — which reimbursement-schedule fields a contract type actually has, with their labels. Built
 * from the slots and `reimbursementSlotLabel`, so the forms and the contract page cannot drift from it.
 */
export const REIMBURSEMENT_FIELDS_BY_CONTRACT_TYPE: Record<
  string,
  { key: string; label: string }[]
> = Object.fromEntries(
  Object.entries(REIMBURSEMENT_SLOTS_BY_CONTRACT_TYPE).map(([type, slots]) => [
    type,
    slots.map((slot) => ({
      key: REIMBURSEMENT_FIELD_KEYS[slot],
      label: reimbursementSlotLabel(slot, type),
    })),
  ])
);

/**
 * CLE-2811 (2026-09-28) — the MPPR field's one label, everywhere it is shown: the contract's own pages
 * (`ContractView`, `ContractFactsFields`) and the AI modeling tool's run form (`fieldSpecs.ts`), save
 * preview (`applyTermPreview.ts`) and "Saved on this contract" rows (`contractOnFile.ts`).
 */
export const MULTIPLE_PROCEDURE_METHODOLOGY =
  "Multiple procedure (MPPR) methodology";

export const CONTRACT_TYPE_OPTIONS = [
  { label: "Hospital", value: "Hospital" },
  { label: "ASC (Ambulatory Surgery Center)", value: "ASC" },
  { label: "Professional (Physician)", value: "Professional" },
  { label: "CAH (Critical Access Hospital)", value: "CAH" },
];

/**
 * CLE-2811 — whether "Model with AI" must ask for the contract type before starting a run. The type is
 * GIVEN to the AI (an ASC run must not model inpatient), so a contract that already records it is
 * never asked; one that does not is asked once, with "not sure" as a real answer.
 */
export const needsContractTypePrompt = (
  contract?: { contractType?: string | null } | null
): boolean => !contract?.contractType;

/**
 * CLE-2811 — the idempotency key for a modeling run started from a contract. Stage-scoped by
 * convention, so a facts run and a full run on the same contract never collide: a double-click returns
 * the facts run in flight, without ever handing back a different stage's run.
 */
export const modelRunIdempotencyKey = (
  contractId: string,
  stage: "facts" | "full"
): string => `contract:${contractId}:${stage}`;

export const reimbursementFieldsFor = (
  contractType?: string | null
): { key: string; label: string }[] =>
  (contractType && REIMBURSEMENT_FIELDS_BY_CONTRACT_TYPE[contractType]) || [];

/** The contract-facts form values, as both contract forms hold them. */
export interface IContractFactsFormValues {
  contractType?: string | null;
  signedDate?: Date | null;
  timelyFilingDays?: number | null;
  inpatientReimbursement?: string;
  outpatientReimbursement?: string;
  facilityReimbursement?: string;
  nonFacilityReimbursement?: string;
  /** CLE-2811 (2026-09-28) — informational; MPPR pricing comes from the adjustment rules. */
  multipleProcedureMethodology?: string;
}

const REIMBURSEMENT_KEYS = [
  "inpatientReimbursement",
  "outpatientReimbursement",
  "facilityReimbursement",
  "nonFacilityReimbursement",
] as const;

/**
 * CLE-2811 — contract facts for a CREATE body: empty fields are omitted entirely, so a contract is
 * created with nulls rather than empty strings.
 */
export const contractFactsPayload = (values: IContractFactsFormValues) => {
  const payload: Record<string, unknown> = {};
  if (values.contractType) payload.contractType = values.contractType;
  // Local calendar day, never toISOString() — see helpers/date. A UTC instant shifts the day for
  // anyone east of Greenwich, and the column is a DATE.
  const signedDate = localCalendarDateToIsoDateString(values.signedDate);
  if (signedDate) payload.signedDate = signedDate;
  if (
    values.timelyFilingDays !== null &&
    values.timelyFilingDays !== undefined
  ) {
    payload.timelyFilingDays = values.timelyFilingDays;
  }
  REIMBURSEMENT_KEYS.forEach((key) => {
    const value = values[key];
    if (value) payload[key] = value;
  });
  if (values.multipleProcedureMethodology?.trim())
    payload.multipleProcedureMethodology = values.multipleProcedureMethodology;
  return payload;
};

/**
 * CLE-2811 — contract facts for an UPDATE body. Unlike the create form this sends every field on
 * every save, including the empty ones, because clearing a box has to actually clear the column.
 * `undefined` leaves a column alone on the API side, which would make a field impossible to unset —
 * the same trap CLE-2781 hit with `nextUpdateDate`.
 *
 * Reimbursement fields a contract type does not use are sent as empty, so switching a contract from
 * Hospital to ASC cannot leave an orphaned inpatient schedule behind that nothing renders.
 */
export const contractFactsUpdatePayload = (
  values: IContractFactsFormValues
) => {
  const visible = new Set(
    reimbursementFieldsFor(values.contractType).map((f) => f.key)
  );
  const payload: Record<string, unknown> = {
    contractType: values.contractType || null,
    signedDate: localCalendarDateToIsoDateString(values.signedDate) ?? null,
    timelyFilingDays:
      values.timelyFilingDays === undefined ? null : values.timelyFilingDays,
  };
  REIMBURSEMENT_KEYS.forEach((key) => {
    payload[key] = visible.has(key) ? values[key] || "" : "";
  });
  // `''` clears the column on the API side (the DTO is a string, so not null). Every contract type has it.
  payload.multipleProcedureMethodology =
    values.multipleProcedureMethodology || "";
  return payload;
};
